import { Injectable, OnDestroy } from '@angular/core';

import { Observable, from, switchMap } from 'rxjs';
import { map, timeout } from 'rxjs/operators';

import { PluginListenerHandle } from '@capacitor/core';

import { CheckPlayServices } from '@metib/check-play-services';
import { ActionPerformed, PushNotificationSchema, PushNotifications } from '@metib/push-notifications';

import { DeviceService } from '@core/services/device/device.service';
import { PushNotificationsPlatformService } from '@core/services/push-notifications/platforms/push-notifications-platform.service';
import { PushNotificationsPermissionError } from '@core/services/push-notifications/push-notifications.error';
import { PushNotificationPlatformType } from '@core/services/push-notifications/push-notifications.types';

@Injectable()
export class PushNotificationsPlatformNativeService extends PushNotificationsPlatformService implements OnDestroy {
  platform: PushNotificationPlatformType = DeviceService.isAndroid ? 'firebase' : 'apns';
  private readonly REGISTRATION_TIMEOUT_MS = 30_000;

  constructor() {
    super();
  }

  addListeners() {
    this.isAvailable().then((available) => {
      if (available) {
        PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
          // //  TODO метод на андроиде почему то помечает все как прочитанные...
          // // this.markAsRead(notification.event_id, notification.cloud_id);
          // if (this.confirmService.pushHasConfirmCode(notification.title)) {
          //   this.confirmService.setConfirmCodeFromPush(notification.body);
          // }
          this.notificationReceived$.next(notification);
        });

        PushNotifications.addListener('pushNotificationActionPerformed', (actionPerformed: ActionPerformed) => {
          this.notificationActionPerformed$.next(actionPerformed.notification);
        });
      }
    });
  }

  async isAvailable(): Promise<boolean> {
    if (DeviceService.isAndroid) {
      const value = await CheckPlayServices.isAvailable();
      return value.available;
    }
    return Promise.resolve(true);
  }

  ngOnDestroy() {
    this.isAvailable().then((value) => {
      if (value) {
        PushNotifications.removeAllListeners();
      }
    });
  }

  override registration(): Observable<string | null> {
    return this.requestPermission().pipe(
      switchMap((granted) => {
        if (!granted) {
          throw new PushNotificationsPermissionError('Уведомления не разрешены на устройстве');
        }

        return new Observable<string>((observer) => {
          const register = async () => {
            const promises = [
              PushNotifications.addListener('registration', (token) => {
                observer.next(token.value);
                observer.complete();
              }),
              PushNotifications.addListener('registrationError', (err) => {
                observer.error(err);
              })
            ];

            listeners.push(...(await Promise.all(promises)));
            await PushNotifications.register();
            this.addListeners();
          };
          const listeners: PluginListenerHandle[] = [];

          void register().catch((error) => observer.error(error));

          return () => listeners.forEach((listener) => listener.remove());
        }).pipe(timeout(this.REGISTRATION_TIMEOUT_MS));
      })
    );
  }

  override checkPermission(): Observable<boolean> {
    return from(PushNotifications.checkPermissions()).pipe(map((permStatus) => permStatus.receive === 'granted'));
  }

  override unregister() {
    const promises = [PushNotifications.removeAllListeners(), PushNotifications.unregister()];

    return from(Promise.all(promises));
  }

  override goToSettings() {
    void PushNotifications.goToSettings();
  }

  override generateKey(): Observable<string> {
    return from(PushNotifications.generateKeys()).pipe(map((res: { key: string }) => res.key.replace(/ RSA/g, '')));
  }

  override setUrl(url: string) {
    PushNotifications.setUrl({ url });
  }

  private requestPermission(): Observable<boolean> {
    const requestPermission = async () => {
      let permStatus = await PushNotifications.checkPermissions();

      if (permStatus.receive === 'prompt') {
        permStatus = await PushNotifications.requestPermissions();
      }

      return permStatus.receive === 'granted';
    };

    return from(requestPermission());
  }
}
